import { com } from '@eidu/web'
import { findFieldById } from '../../../util/EntityTypeExtensions'
import EntityPath = com.eidu.entity.path.EntityPath
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntityType = com.eidu.entity.type.EntityType
import FieldId = com.eidu.entity.field.FieldId
import EdgeDirection = com.eidu.entity.path.EdgeDirection
import FieldType = com.eidu.entity.field.FieldType

const getEntityTypeById = (entityTypes: readonly EntityType[], id: EntityTypeId) =>
  entityTypes.find((type) => type.id.equals(id))

const getEntityPathLabel = (entityTypes: readonly EntityType[], path: EntityPath): string =>
  (getEntityTypeById(entityTypes, path.fromType)?.name ?? 'Unknown from type') +
  path.edges.asJsReadonlyArrayView().reduce((label, edge, index, edgesArr) => {
    const entityType = getEntityTypeById(entityTypes, edge.nextType)
    if (!entityType) return label + 'Unknown'

    const getEdgeFieldName = () => {
      const getFieldName = (type: EntityType | undefined, fieldId: FieldId, fallback: string) =>
        type ? findFieldById(type, fieldId)?.name ?? fallback : fallback

      if (index === 0) {
        if (edge.direction === EdgeDirection.Incoming) {
          return getFieldName(entityType, edge.field, 'Unknown Field')
        } else return getFieldName(getEntityTypeById(entityTypes, path.fromType), edge.field, 'Unknown user Field')
      } else if (edge.direction === EdgeDirection.Outgoing) {
        const prevEdge = edgesArr[index - 1]
        return getFieldName(getEntityTypeById(entityTypes, prevEdge.nextType), edge.field, 'Unknown Outgoing Field')
      } else {
        return getFieldName(entityType, edge.field, 'Unknown Incoming Field')
      }
    }

    const fieldName = getEdgeFieldName()
    const fieldLabel = `[${fieldName}]`

    const arrow =
      edge.direction === EdgeDirection.Outgoing
        ? `${fieldLabel} > ${entityType.name}`
        : ` < ${entityType.name}${fieldLabel}`

    return label + arrow
  }, '')

export const pathContainsReferenceSet = (entityTypes: readonly EntityType[], path: EntityPath): boolean => {
  let previousTypeId = path.fromType
  return path.edges.asJsReadonlyArrayView().some((edge) => {
    const ownerTypeId = edge.direction === EdgeDirection.Outgoing ? previousTypeId : edge.nextType
    previousTypeId = edge.nextType
    const owner = getEntityTypeById(entityTypes, ownerTypeId)
    return owner?.let((it) => findFieldById(it, edge.field)?.type === FieldType.ReferenceSet) ?? false
  })
}

export type EntityPathNode = {
  id: string
  type: string
  leftField?: string
  rightField?: string
}

export const transformEntityPath = (
  entityTypes: readonly EntityType[],
  path: EntityPath
): readonly EntityPathNode[] => {
  const baseId = crypto.randomUUID()

  let type = getEntityTypeById(entityTypes, path.fromType)
  const nodes: EntityPathNode[] = [{ id: `${baseId}-0`, type: type?.name ?? path.fromType.toString() }]

  path.edges.asJsReadonlyArrayView().forEach((edge, index) => {
    const nextType = getEntityTypeById(entityTypes, edge.nextType)
    if (edge.direction === EdgeDirection.Outgoing) {
      nodes[nodes.length - 1].rightField =
        type?.let((it) => findFieldById(it, edge.field)?.name) ?? edge.field.toString()
      nodes.push({ id: `${baseId}-${index + 1}`, type: nextType?.name ?? edge.nextType.toString() })
    } else if (edge.direction === EdgeDirection.Incoming) {
      nodes.push({
        id: `${baseId}-${index + 1}`,
        type: nextType?.name ?? edge.nextType.toString(),
        leftField: nextType?.let((it) => findFieldById(it, edge.field)?.name) ?? edge.field.toString(),
      })
    }

    type = nextType
  })

  return nodes
}

export default getEntityPathLabel
